import { useState } from "react";
import { useCinematic } from "@/data/cinematicMode/context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Save, Trash2, FolderOpen, CheckCircle2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export function DemoPresetsPanel() {
  const { savedPresets, savePreset, loadPreset, deletePreset, activePresetId } = useCinematic();
  const [newLabel, setNewLabel] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);

  const handleSave = () => {
    const label = newLabel.trim();
    if (!label) return;
    savePreset(label);
    setNewLabel("");
    setShowSaveInput(false);
  };

  return (
    <div className="space-y-4">
      {/* Save current */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Saved Demos
        </h3>
        {!showSaveInput && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs h-7"
            onClick={() => setShowSaveInput(true)}
          >
            <Plus className="h-3 w-3" />
            Save Current
          </Button>
        )}
      </div>

      {showSaveInput && (
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-card">
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g. Investor Demo, Sales Training..."
            className="h-8 text-xs flex-1"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />
          <Button size="sm" className="gap-1.5 text-xs h-8" onClick={handleSave} disabled={!newLabel.trim()}>
            <Save className="h-3 w-3" />
            Save
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-8" onClick={() => { setShowSaveInput(false); setNewLabel(""); }}>
            Cancel
          </Button>
        </div>
      )}

      {/* Preset list */}
      {savedPresets.length === 0 ? (
        <div className="text-center py-8 text-xs text-muted-foreground/60">
          No saved demos yet. Customize your scenes and save them as a preset.
        </div>
      ) : (
        <div className="space-y-2">
          {savedPresets.map((preset) => {
            const isActive = preset.id === activePresetId;
            return (
              <div
                key={preset.id}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors",
                  isActive
                    ? "bg-primary/5 border-primary/30"
                    : "bg-card hover:bg-accent/30"
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{preset.label}</p>
                    {isActive && (
                      <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] gap-1">
                        <CheckCircle2 className="h-2.5 w-2.5" /> Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground/60 font-mono mt-0.5">
                    {preset.scenes.length} scenes · {new Date(preset.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-xs h-7"
                    onClick={() => loadPreset(preset.id)}
                    disabled={isActive}
                  >
                    <FolderOpen className="h-3 w-3" />
                    Load
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 text-destructive hover:text-destructive"
                    onClick={() => deletePreset(preset.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
